/* global klevu */

/**
 * Mobile search drawer (snippets/mobile-search-drawer.liquid).
 *
 * Klevu quick search attaches to the drawer's input[name=q] like any other search box. This
 * component moves that box's Klevu results popup into the drawer and re-arranges it after every
 * render: products, categories, information pages and blog posts, plus a fixed "View all {x}
 * results" button. Klevu still makes the request and renders the results, so its analytics, the
 * market price conversion (klevu-globale-main) and the dataLayer events (klevu-datalayer) are kept.
 */
if (!customElements.get('mobile-search-drawer')) {
  class MobileSearchDrawer extends HTMLElement {
    connectedCallback() {
      // The header can be sticky/transformed, which breaks position: fixed for its children.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.initialised) return;
      this.initialised = true;

      this.input = this.querySelector('.mobile-search-drawer__input');
      this.results = this.querySelector('.js-mobile-search-results');
      this.footer = this.querySelector('.mobile-search-drawer__footer');
      this.viewAll = this.querySelector('.mobile-search-drawer__view-all');
      this.labels = {
        products: this.dataset.productsLabel,
        categories: this.dataset.categoriesLabel,
        information: this.dataset.informationLabel,
        blog: this.dataset.blogLabel,
        viewAll: this.dataset.viewAllLabel
      };

      document.addEventListener('click', (evt) => {
        const opener = evt.target.closest('.js-open-mobile-search');
        if (!opener || theme.mediaMatches.md) return;
        evt.preventDefault();
        this.open(opener);
      });
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('.js-close-mobile-search')) this.close();
      });
      this.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') this.close();
      });
      this.input.addEventListener('input', () => {
        if (!this.input.value.trim()) this.footer.hidden = true;
      });
      window.addEventListener('on:breakpoint-change', () => {
        if (theme.mediaMatches.md) this.close();
      });

      this.hookKlevu();
    }

    /**
     * Opens the drawer. Focuses the input straight away (inside the tap) so iOS shows the keyboard.
     * @param {Element} opener - Element that opened the drawer, focused again on close.
     */
    open(opener) {
      this.opener = opener;
      this.setAttribute('open', '');
      document.documentElement.classList.add('mobile-search-open');
      this.input.focus();
      this.attachResults();
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.documentElement.classList.remove('mobile-search-open');
      this.input.blur();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }

    /**
     * Moves the Klevu results popup of the drawer input into the drawer body.
     * Klevu gives the popup the same id as the input's Klevu scope.
     * @returns {?Element} The Klevu popup, once Klevu has created it.
     */
    attachResults() {
      const scopeId = this.input.kScope && this.input.kScope.id;
      const target = scopeId ? document.getElementById(scopeId) : null;
      if (target && target.parentElement !== this.results) this.results.appendChild(target);
      return target;
    }

    hookKlevu() {
      let tries = 0;
      const tryHook = () => {
        if (!(window.klevu && klevu.search && klevu.search.extraSearchBox)) return false;

        let box = null;
        klevu.each(klevu.search.extraSearchBox, (key, searchBox) => {
          if (searchBox.getScope().element === this.input) box = searchBox;
        });
        if (!box) return false;

        const { chains } = box.getScope();

        // Klevu returns 3 CMS results by default; ask for more so pages and blog posts both show.
        chains.request.build.add({
          name: 'cpMobileDrawerRequest',
          fire: (data) => {
            klevu.each(data.request.current.recordQueries || [], (k, query) => {
              if (query.id === 'cmsCompressed') klevu.setObjectPath(query, 'settings.limit', 6);
            });
          }
        });

        chains.template.events.add({
          name: 'cpMobileDrawerRender',
          fire: (data) => {
            try {
              this.render(data);
            } catch (err) {
              console.error('[mobile-search-drawer]', err); // eslint-disable-line no-console
            }
          }
        });
        return true;
      };

      (function wait() {
        if (!tryHook() && tries++ < 300) setTimeout(wait, 100);
      })();
    }

    /**
     * Re-arranges the Klevu popup after each render.
     * @param {object} data - Klevu template data (request and response).
     */
    render(data) {
      const target = this.attachResults();
      if (!target) return;

      // Empty box: leave Klevu's trending/recently viewed popup as it is.
      if (!this.input.value.trim()) {
        this.footer.hidden = true;
        return;
      }

      const totals = {};
      ((data.response && data.response.current && data.response.current.queryResults) || []).forEach((query) => {
        if (query && query.meta) totals[query.id] = Number(query.meta.totalResultsFound) || 0;
      });

      MobileSearchDrawer.setHeading(target.querySelector('.klevuResultsBlock'), this.labels.products, totals.productList);
      MobileSearchDrawer.setHeading(target.querySelector('#klevuCategoryArea'), this.labels.categories, totals.categoryCompressed);
      this.splitCmsResults(target);
      this.updateFooter(target, totals.productList);
    }

    /**
     * Klevu returns information pages and blog posts as one "Pages" list: show them separately.
     * @param {Element} target - Klevu popup.
     */
    splitCmsResults(target) {
      target.querySelectorAll('.cp-msd-blog').forEach((el) => el.remove());

      const cms = target.querySelector('#klevuCmsContentArea');
      if (!cms) return;

      const items = Array.from(cms.querySelectorAll('li'));
      const blogItems = items.filter((li) => {
        const link = li.querySelector('a');
        return link && /\/blogs\//.test(link.getAttribute('href') || '');
      });

      MobileSearchDrawer.setHeading(cms, this.labels.information);
      cms.classList.toggle('cp-msd-empty', !items.length || items.length === blogItems.length);
      if (!blogItems.length) return;

      const blog = document.createElement('div');
      blog.className = 'klevuAutoSuggestionsWrap cp-msd-blog';
      blog.innerHTML = '<div class="klevuSuggestionHeading"><span class="klevuHeadingText"></span></div><ul></ul>';
      MobileSearchDrawer.setHeading(blog, this.labels.blog);
      const list = blog.querySelector('ul');
      blogItems.forEach((li) => list.appendChild(li));
      cms.after(blog);
    }

    /**
     * Shows the fixed "View all {x} results" button when there is a search term with results.
     * @param {?Element} target - Klevu popup.
     * @param {number} [total] - Number of products found.
     */
    updateFooter(target, total) {
      const term = this.input.value.trim();
      if (!target || !term || !total) {
        this.footer.hidden = true;
        return;
      }

      const klevuViewAll = target.querySelector('.klevuProductsViewAll a');
      this.viewAll.href = (klevuViewAll && klevuViewAll.getAttribute('href'))
        || `/pages/search-results?q=${encodeURIComponent(term)}`;
      this.viewAll.textContent = this.labels.viewAll.replace('[count]', MobileSearchDrawer.formatNumber(total));
      this.footer.hidden = false;
    }

    /**
     * Sets a Klevu section heading to "Label (count)".
     * @param {?Element} section - Klevu section containing a .klevuHeadingText.
     * @param {string} label - Heading text.
     * @param {number} [count] - Total results for the section.
     */
    static setHeading(section, label, count) {
      const heading = section && section.querySelector('.klevuHeadingText');
      if (!heading) return;

      heading.textContent = label;
      if (count) {
        const countEl = document.createElement('span');
        countEl.className = 'cp-msd-count';
        countEl.textContent = ` (${MobileSearchDrawer.formatNumber(count)})`;
        heading.appendChild(countEl);
      }
    }

    static formatNumber(number) {
      return Number(number).toLocaleString(document.documentElement.lang || undefined);
    }
  }

  customElements.define('mobile-search-drawer', MobileSearchDrawer);
}
